// @mui material components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import CustomerListing from "./components/CustomerListing";
import { useEffect, useState } from "react";
import NontAuthorized401 from "NontAuthorized401";
import { DatePicker } from "antd";
import Pagination from "components/pagination";
import { apiCall } from "utils/apiClient";
import * as XLSX from "xlsx";

const buildCustomerFilters = (page, limit, search, dateRange, quickFilter) => ({
  page,
  limit,
  search: search || undefined,
  startDate: dateRange[0]?.format("YYYY-MM-DD"),
  endDate: dateRange[1]?.format("YYYY-MM-DD"),
  filter: quickFilter || undefined,
});

function CustomerList() {
  const [customer, setCustomer] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateRange, setDateRange] = useState([]);
  const [quickFilter, setQuickFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pageCount, setPageCount] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exportingExcel, setExportingExcel] = useState(false);

  // Debounce for search
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  // API Fetch
  const getAllCustomer = async () => {
    setLoading(true);
    setCustomer([]);
    setPageCount(0);
    setTotalRecords(0);
    try {
      const body = {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearch || undefined,
        startDate: dateRange[0]?.format("YYYY-MM-DD"),
        endDate: dateRange[1]?.format("YYYY-MM-DD"),
        filter: quickFilter || undefined,
      };

      const result = await apiCall({ endpoint: "customer/getCustomer", method: "POST", data: body });

      if (result.status === 200) {
        setCustomer(result.data || []);
        setPageCount(result.pagination.total_pages || 0);
        setTotalRecords(result.pagination.total_records || 0);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCustomers = async () => {
    const batchSize = 200;
    let page = 1;
    let totalPagesSeen = 1;
    const allCustomers = [];

    while (page <= totalPagesSeen) {
      const body = buildCustomerFilters(page, batchSize, debouncedSearch, dateRange, quickFilter);
      const result = await apiCall({ endpoint: "customer/getCustomer", method: "POST", data: body });

      if (result.status !== 200) {
        throw new Error(result?.message || "Unable to fetch customer export data.");
      }

      const rows = Array.isArray(result.data) ? result.data : [];
      allCustomers.push(...rows);
      totalPagesSeen = Number(result.pagination?.total_pages || totalPagesSeen || 1);

      if (rows.length < batchSize && !result.pagination?.total_pages) {
        break;
      }

      page += 1;
    }

    return allCustomers;
  };

  const exportToExcel = async () => {
    setExportingExcel(true);

    try {
      const rows = await fetchAllCustomers();

      if (rows.length === 0) {
        return;
      }

      const sheetRows = rows.map((item, index) => ({
        "Sr No": index + 1,
        Name: item.name || "-",
        Mobile: item.mobile || item.mobile_no || "-",
        Email: item.email || "-",
        Status: item.status || "-",
        Filter: quickFilter || "-",
        "Created At": item.created_at || item.createdAt || "-",
        "Updated At": item.updated_at || item.updatedAt || "-",
      }));

      const worksheet = XLSX.utils.json_to_sheet(sheetRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");

      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `customers-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting customers:", error);
    } finally {
      setExportingExcel(false);
    }
  };

  useEffect(() => {
    getAllCustomer();
  }, [debouncedSearch, dateRange, quickFilter, currentPage, itemsPerPage]);

  // Reset filters
  const clearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setDateRange([]);
    setQuickFilter("");
    setCurrentPage(1);
  };

  return localStorage.getItem("Token") ? (
    <DashboardLayout>
      <DashboardNavbar />

      {/* Outer Bordered Container (matches Booking style) */}
      <div className="border-solid border-2 black-indigo-600 mt-4 rounded-lg">
        <div className="px-4 py-3 text-base font-semibold">Customer</div>
        <div className="flex flex-col gap-2 px-3 py-3 bg-white rounded-lg shadow mx-3 mt-2">
          {/* Search Input */}
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-full sm:w-56">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search Customer..."
                className="w-full border px-3 py-1.5 text-sm rounded-md"
              />
            </div>

            {/* Date Range */}
            <div className="w-full sm:w-64">
              <DatePicker.RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates || [])}
                format="YYYY-MM-DD"
                size="small"
                className="w-full"
              />
            </div>

            {/* Quick Filters */}
            <div className="flex flex-wrap gap-1.5">
              {["today", "this_week","last_week" ,"this_month","last_month"].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setQuickFilter(filter)}
                  className={`px-2.5 py-1 text-xs rounded ${quickFilter === filter
                    ? "bg-blue-700 text-white"
                    : "bg-blue-300 text-white hover:bg-blue-600"
                    }`}
                >
                  {filter === "today" && "Today"}
                  {filter === "this_week" && "This Week"}
                  {filter === "this_month" && "This Month"}
                  {filter === "last_week" && "Last Week"}
                  {filter === "last_month" && "Last Month"}
                </button>
              ))}
            </div>

            {/* Clear Filters */}
            <div className="w-auto ml-auto">
              <button
                onClick={exportToExcel}
                disabled={exportingExcel || loading}
                className="mr-2 flex items-center gap-1 text-xs px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white disabled:cursor-not-allowed disabled:bg-emerald-300"
              >
                {exportingExcel ? "Exporting..." : "Export Excel"}
              </button>
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-xs px-2.5 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-800"
              >
                <span>&#x2715;</span> Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Table Listing */}
        <div className="px-3 pt-3">
          <CustomerListing
            customer={customer}
            loading={loading}
            getAllCustomer={getAllCustomer}
          />
        </div>

        {/* Pagination */}
        <div className="px-3 py-3">
          <Pagination
            currentPage={currentPage}
            totalPages={pageCount}
            onPageChange={(number) => setCurrentPage(number)}
            recordsPerPage={itemsPerPage}
            onRecordsPerPageChange={(value) => {
              setCurrentPage(1);
              setItemsPerPage(value);
            }}
            totalRecords={totalRecords}
            count={customer.length}
          showFirstLastControls
          compact
          />
        </div>
      </div>
    </DashboardLayout>
  ) : (
    <NontAuthorized401 />
  );
}

export default CustomerList;

